/** The `criteria` area: the search criteria bar and search result rows. Owned
 *  by the stage 2 slot components-a (docs/I18N.md §11). */
export const criteria = {
  // The attributes a filter narrows by (`ATTRIBUTE_LABEL`).
  'criteria.attribute.active': 'Active',
  'criteria.attribute.closed': 'Conclusion',
  'criteria.attribute.level': 'Health',
  'criteria.attribute.assignee': 'Contributor',
  /** The `party` attribute: a requester or a contributor (glossary: parties). */
  'criteria.attribute.party': 'Parties',
  'criteria.attribute.group': 'Group',
  'criteria.attribute.parent': 'Parent',
  'criteria.attribute.title': 'Title',
  'criteria.attribute.tag': 'Tag',
  'criteria.attribute.tendered': 'Done or in review',
  'criteria.attribute.judgment': 'Verdict',
  'criteria.attribute.cancelled': 'Cancelled',
  'criteria.attribute.done': 'Done',
  // The values the bar suggests.
  'criteria.value.yes': 'yes',
  'criteria.value.no': 'no',
  'criteria.value.noneYet': 'none yet',
  'criteria.value.accepted': 'Accepted',
  'criteria.value.denied': 'Denied',
  /** A `closed: yes` criterion: both endings at once. */
  'criteria.value.doneOrCancelled': 'Done or cancelled',
  // The empty value of a set-valued attribute, the first row of its values.
  'criteria.value.unclaimed': 'Unclaimed',
  /** No seat held at all, neither requester nor contributor. */
  'criteria.value.noParties': 'No parties',
  'criteria.value.noTag': 'No tag',
  'criteria.value.noGroup': 'No group',
  'criteria.value.noParent': 'No parent',
  'criteria.value.levelAtLeast': '≥ {level}',
  /** A negated level row, as the open mode's leading minus offers it. */
  'criteria.value.levelBelow': '< {level}',
  'criteria.value.titleContains': 'contains “{text}”',
  /** The negated title row; `{not}` is `criteria.word.not`, which the bar
   *  sets in the accent. */
  'criteria.value.titleNotContains': '{not} containing “{text}”',
  /** The word "not" alone, inside a sentence that places it itself. */
  'criteria.word.not': 'not',
  'criteria.value.tag': '#{tag}',
  // A health grade, as the level filter names it.
  'criteria.level.info': 'info',
  'criteria.level.notice': 'notice',
  'criteria.level.warning': 'warning',
  'criteria.level.critical': 'critical',
  // An applied criterion's chip.
  /** A chip that names an attribute: `{attribute}` and `{value}` are its
   *  parts, the template's own words its operator (the chip styles each). */
  'criteria.chip.is': '{attribute}: {value}',
  /** The negated reading of `criteria.chip.is`; the sign wears the accent. */
  'criteria.chip.isNot': '{attribute} ≠ {value}',
  /** The parent attribute's "contains": the value is an ancestor at any
   *  depth (the `ancestor` criterion), never the item itself. */
  'criteria.chip.contains': '{attribute} contains {value}',
  'criteria.chip.containsNot': '{attribute} does not contain {value}',
  'criteria.chip.levelAtLeast': '{attribute} ≥ {value}',
  /** The negated level: below the grade, an item with no signal included. */
  'criteria.chip.levelBelow': '{attribute} < {value}',
  'criteria.chip.title': '“{text}”',
  /** A negated chip with no attribute word: a tag, a title ("not #legacy"). */
  'criteria.chip.not': 'not {value}',
  /** Two values of one chip; a longer list nests ("#a or #b or #c"). */
  'criteria.chip.or': '{first} or {second}',
  // The negated empty criterion: the attribute holds something.
  'criteria.chip.hasTag': 'Has a tag',
  'criteria.chip.hasContributor': 'Has a contributor',
  'criteria.chip.hasParties': 'Has parties',
  'criteria.chip.inAGroup': 'In a group',
  'criteria.chip.hasParent': 'Has a parent',
  // The bar itself.
  'criteria.bar.label': 'Filter',
  'criteria.bar.placeholder': 'Filter…',
  'criteria.bar.removeFilter': 'Remove filter {label}',
  'criteria.bar.combine': 'How the filters combine',
  'criteria.bar.all': 'all',
  'criteria.bar.any': 'any',
  /** The lead token while the bar offers one attribute's values. */
  'criteria.bar.pending': '{attribute}:',
  'criteria.bar.suggestions': 'Filter suggestions',
  'criteria.bar.attributeHint': 'attribute…',
  'criteria.bar.searchEverywhere': 'Search everywhere',
  /** The tooltip on a title chip's words, which put its text back into the
   *  input. */
  'criteria.bar.editTerm': 'Edit the search term',
  /** The pills on the active row in value mode: add the value negated, or
   *  join it to the attribute's chip. */
  'criteria.bar.exclude': 'Exclude',
  'criteria.bar.or': 'Or',
  /** The filter button at the end of the box: its name and tooltip, with
   *  the count of the criteria the chip row below it shows or hides. */
  'criteria.bar.filters': { one: '{count} filter', other: '{count} filters' },
  /** The footer hint in value mode; `{shift}` and `{alt}` are the modifier
   *  labels below, or the Mac glyphs. */
  'criteria.bar.hint': '↵ filter to · {shift}↵ exclude · type − before a value',
  'criteria.bar.hintWithOr': '↵ filter to · {shift}↵ exclude · {alt}↵ or · type − before a value',
  /** The modifier keys as a hint names them off a Mac ("Shift+↵"). */
  'criteria.key.shift': 'Shift+',
  'criteria.key.alt': 'Alt+',
  // A chip's readings: the menu of states of a boolean or an empty chip, and
  // the state control of the chip editor (the ✕ removes).
  /** The menu's or the editor's accessible name, and its sheet's on a
   *  phone. */
  'criteria.menu.label': 'Filter {label}',
  'criteria.menu.is': 'is',
  'criteria.menu.isNot': 'is not',
  'criteria.menu.isAnyOf': 'is any of',
  'criteria.menu.isNoneOf': 'is none of',
  'criteria.menu.atLeast': 'at least',
  'criteria.menu.below': 'below',
  // An attribute chip's editor (docs/SEARCH.md "The bar"): the state control
  // (the `criteria.menu.*` readings above), a parent's depth, a search field,
  // the value rows.
  /** The state control's accessible name. */
  'criteria.editor.comparison': 'Comparison',
  /** A parent chip's second control: the direct parent only, or any level
   *  above it too (the `ancestor` criterion). */
  'criteria.editor.depth': 'Depth',
  'criteria.editor.direct': 'direct',
  'criteria.editor.indirect': 'indirect too',
  /** The search field over the value rows, per attribute. */
  'criteria.editor.searchPeople': 'Search people…',
  'criteria.editor.searchGroups': 'Search groups…',
  'criteria.editor.searchItems': 'Search items…',
  'criteria.editor.searchTags': 'Search tags…',
  /** The line under a search that leaves no row. */
  'criteria.editor.noMatches': 'No matches.',
  // The pager under a page of hits.
  'criteria.pager.previous': 'Previous',
  'criteria.pager.next': 'Next',
  'criteria.pager.range': '{first}-{last} of {total}',
  'criteria.pager.empty': '0 of {total}',
} as const;
